import { useEffect, useState } from 'react';
import { FolderTree } from 'lucide-react';
import type { CategoryRef } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TaxonomyPicker } from './taxonomy-picker';
import { useBulkCategoryPosts } from '@/lib/posts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postIds: string[];
  canCreate: boolean;
  onApplied?: () => void;
}

/**
 * Sibling of BulkTagDialog — same UX, operates on the category
 * junction. Two pickers (adds + removes), submit disabled until at
 * least one has content. Resets on close so reopening is a clean slate.
 */
export function BulkCategoryDialog({ open, onOpenChange, postIds, canCreate, onApplied }: Props) {
  const [adds, setAdds] = useState<CategoryRef[]>([]);
  const [removes, setRemoves] = useState<CategoryRef[]>([]);
  const mutation = useBulkCategoryPosts();

  useEffect(() => {
    if (open) {
      setAdds([]);
      setRemoves([]);
    }
  }, [open]);

  const canSubmit = adds.length + removes.length > 0 && !mutation.isPending;

  const submit = () => {
    if (!canSubmit) return;
    mutation.mutate(
      {
        ids: postIds,
        addCategoryIds: adds.length > 0 ? adds.map((c) => c.id) : undefined,
        removeCategoryIds: removes.length > 0 ? removes.map((c) => c.id) : undefined,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onApplied?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderTree className="h-5 w-5 text-slate-400" />
            แก้หมวด {postIds.length} บทความพร้อมกัน
          </DialogTitle>
          <DialogDescription>
            เพิ่มหรือลบหมวด ไม่กระทบหมวดอื่นที่อยู่บนบทความ — หมวดที่มีอยู่แล้วจะข้าม
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <TaxonomyPicker
            kind="category"
            label="เพิ่มหมวด"
            selected={adds}
            onChange={(next) => setAdds(next)}
            canCreate={canCreate}
            placeholder="เลือกหรือสร้างหมวดที่จะเพิ่ม"
          />
          <TaxonomyPicker
            kind="category"
            label="ลบหมวด"
            selected={removes}
            onChange={(next) => setRemoves(next)}
            canCreate={false}
            placeholder="เลือกหมวดที่จะลบ"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            ยกเลิก
          </Button>
          <Button type="button" onClick={submit} disabled={!canSubmit}>
            {mutation.isPending ? 'กำลังอัปเดต...' : 'อัปเดต'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
